import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import {
    Users, HandHeart, Gift, PencilLine, Heart, Footprints, LogOut, Loader2, RefreshCw,
} from "lucide-react";

const TABS = [
    { id: "registrations", label: "Registrations", icon: Users, color: "bg-brand-purple", endpoint: "/admin/registrations" },
    { id: "sponsors", label: "Sponsors", icon: Gift, color: "bg-brand-blue", endpoint: "/admin/sponsors" },
    { id: "volunteers", label: "Volunteers", icon: HandHeart, color: "bg-brand-lime", endpoint: "/admin/volunteers" },
    { id: "notes", label: "Notes", icon: PencilLine, color: "bg-brand-red", endpoint: "/admin/notes" },
    { id: "fivek_notifications", label: "5K Notify", icon: Footprints, color: "bg-stone-900", endpoint: "/admin/fivek-notifications" },
    { id: "payment_transactions", label: "Donations", icon: Heart, color: "bg-brand-purple-light", endpoint: "/admin/donations" },
];

function fmt(v) {
    if (v == null) return "—";
    if (typeof v === "number") return v.toString();
    if (typeof v === "string" && /\d{4}-\d{2}-\d{2}T/.test(v)) {
        try { return new Date(v).toLocaleString(); } catch (e) { return v; }
    }
    return String(v);
}

function DataTable({ items, hiddenKeys = ["id", "metadata"] }) {
    if (!items || items.length === 0) {
        return <div className="text-stone-500 text-sm py-10 text-center" data-testid="admin-empty">No entries yet.</div>;
    }
    const keys = Object.keys(items[0]).filter((k) => !hiddenKeys.includes(k));
    return (
        <div className="overflow-x-auto rounded-2xl border border-stone-200 bg-brand-cream">
            <table className="min-w-full text-sm" data-testid="admin-table">
                <thead className="bg-stone-100">
                    <tr>
                        {keys.map((k) => (
                            <th key={k} className="px-4 py-3 text-left font-medium text-stone-600 uppercase tracking-wider text-xs">{k.replace(/_/g, " ")}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {items.map((row, idx) => (
                        <tr key={idx} className="border-t border-stone-200 align-top hover:bg-stone-50">
                            {keys.map((k) => (
                                <td key={k} className="px-4 py-3 text-stone-800 max-w-xs break-words">{fmt(row[k])}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function AdminDashboard() {
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();
    const [summary, setSummary] = useState(null);
    const [active, setActive] = useState("registrations");
    const [items, setItems] = useState([]);
    const [loadingTab, setLoadingTab] = useState(false);
    const [refreshNonce, setRefreshNonce] = useState(0);

    useEffect(() => {
        if (!loading && !user) navigate("/admin/login", { replace: true });
    }, [user, loading, navigate]);

    useEffect(() => {
        if (!user) return;
        (async () => {
            try {
                const { data } = await api.get("/admin/summary", { withCredentials: true });
                setSummary(data);
            } catch (e) { /* ignore */ }
        })();
    }, [user, refreshNonce]);

    useEffect(() => {
        if (!user) return;
        const tab = TABS.find((t) => t.id === active);
        if (!tab) return;
        let cancelled = false;
        (async () => {
            setLoadingTab(true);
            try {
                const { data } = await api.get(tab.endpoint, { withCredentials: true });
                if (!cancelled) setItems(data.items || []);
            } catch (e) {
                if (!cancelled) setItems([]);
            } finally {
                if (!cancelled) setLoadingTab(false);
            }
        })();
        return () => { cancelled = true; };
    }, [active, user, refreshNonce]);

    const onLogout = async () => {
        await logout();
        navigate("/admin/login", { replace: true });
    };

    if (loading || !user) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F2E8D5]">
                <Loader2 className="animate-spin text-brand-purple" size={32} />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F2E8D5]" data-testid="admin-dashboard">
            <header className="bg-brand-navy text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-xs tracking-[0.3em] uppercase text-stone-300">Admin Dashboard</div>
                        <div className="font-serif text-2xl">Poppa &amp; Gigi&apos;s <span className="font-accent text-brand-lime-light">Promise</span></div>
                    </div>
                    <div className="flex items-center gap-3">
                        {user.picture && (
                            <img src={user.picture} alt="" className="w-9 h-9 rounded-full border-2 border-white/30" />
                        )}
                        <div className="text-right">
                            <div className="text-sm font-medium" data-testid="admin-user-name">{user.name || user.email}</div>
                            <div className="text-xs text-stone-300">{user.email}</div>
                        </div>
                        <button
                            onClick={onLogout}
                            data-testid="admin-logout"
                            className="ml-2 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 transition text-sm"
                        >
                            <LogOut size={16} /> Sign out
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Summary cards */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {summary && [
                        { label: "Registrations", value: summary.counts.registrations, color: "text-brand-purple" },
                        { label: "Sponsors", value: summary.counts.sponsors, color: "text-brand-blue" },
                        { label: "Volunteers", value: summary.counts.volunteers, color: "text-brand-lime" },
                        { label: "Notes", value: summary.counts.notes, color: "text-brand-red" },
                        { label: "5K Notify", value: summary.counts.fivek_notifications, color: "text-stone-900" },
                        { label: "Donations (paid)", value: summary.donations_paid_count, color: "text-brand-purple-light" },
                        { label: "Donations $ (paid)", value: `$${(summary.donations_paid_total_usd || 0).toFixed(2)}`, color: "text-emerald-700" },
                    ].map((c) => (
                        <div key={c.label} className="bg-brand-cream rounded-2xl p-5 border border-stone-200" data-testid={`admin-summary-${c.label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                            <div className="text-xs uppercase tracking-[0.2em] text-stone-500">{c.label}</div>
                            <div className={`mt-2 font-serif text-3xl ${c.color}`}>{c.value}</div>
                        </div>
                    ))}
                </div>

                {/* Tabs */}
                <div className="mt-8 flex flex-wrap gap-2">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setActive(t.id)}
                            data-testid={`admin-tab-${t.id}`}
                            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border transition ${
                                active === t.id ? `${t.color} text-white border-transparent` : "bg-brand-cream text-stone-700 border-stone-200 hover:bg-stone-50"
                            }`}
                        >
                            <t.icon size={16} />
                            <span className="text-sm font-medium">{t.label}</span>
                        </button>
                    ))}
                    <button
                        onClick={() => setRefreshNonce((n) => n + 1)}
                        data-testid="admin-refresh"
                        className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-cream border border-stone-200 text-stone-700 hover:bg-stone-50 transition"
                    >
                        <RefreshCw size={14} /> <span className="text-sm">Refresh</span>
                    </button>
                </div>

                {/* Table */}
                <div className="mt-6">
                    {loadingTab ? (
                        <div className="py-16 flex justify-center">
                            <Loader2 className="animate-spin text-brand-purple" size={28} />
                        </div>
                    ) : (
                        <DataTable items={items} />
                    )}
                </div>
            </main>
        </div>
    );
}
